import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  AttachmentInput,
  AttachmentType,
  DocumentStatus,
  StoragePathType,
  useGetDocumentQuery,
  useProcessCompanionChatAttachmentMutation,
} from "../../graphql/generated/graphql";
import useFileUpload, { ProcessedFile } from "../../hooks/useFileUpload";
import { AddTaskModal, Todo } from "../../components/chatNewInterface/interfaceComponents/modals/AddTaskModal";
import {
  AddAssignmentModal,
  Assignment,
} from "../../components/chatNewInterface/interfaceComponents/modals/AddAssignmentModal";
import ChatInput from "./ChatInput";

interface ChatInputControllerProps {
  onSubmit: (message: string, attachment: AttachmentInput | null) => void;
  disabled?: boolean;
  placeholder?: string;
  activeWorkspaceType: "general" | "course" | "support";
  showSuggestions?: boolean;
}

export default function ChatInputController({
  onSubmit,
  disabled,
  placeholder,
  activeWorkspaceType,
  showSuggestions = true,
}: ChatInputControllerProps) {
  const [value, setValue] = useState("");

  // ---------- attachment state ----------
  const [attachment, setAttachment] = useState<AttachmentInput | null>(null);

  // ---------- file state ----------
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("");

  // ---------- task / assignment ----------
  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
  const [isAssignmentModalVisible, setIsAssignmentModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Todo | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);

  const [processAttachment] = useProcessCompanionChatAttachmentMutation();

  // ---------- document polling ----------
  const isDocument = attachment?.type === AttachmentType.Document;
  const documentId = isDocument ? attachment.id : "";

  const { data, startPolling, stopPolling } = useGetDocumentQuery({
    variables: { id: documentId },
    skip: !isDocument,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (isDocument && isFileProcessing) {
      startPolling(3000);
      return () => stopPolling();
    }
  }, [isDocument, isFileProcessing, startPolling, stopPolling]);

  useEffect(() => {
    const doc = data?.getDocument?.data;
    if (!doc) return;

    if (doc.status === DocumentStatus.Completed) {
      setIsFileProcessing(false);
      stopPolling();
    }

    if (doc.status === DocumentStatus.Failed) {
      removeAttachment();
    }
  }, [data]);

  // ---------- task / assignment ----------
  const handleSelectTask = (task: Todo) => {
    setSelectedTask(task);
    setSelectedAssignment(null);
    setAttachment({ type: AttachmentType.Todo, id: task.id });
    setIsTaskModalVisible(false);
  };

  const handleSelectAssignment = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setSelectedTask(null);
    setAttachment({ type: AttachmentType.Assignment, id: assignment.id });
    setIsAssignmentModalVisible(false);
  };

  const removeAttachment = useCallback(() => {
    setAttachment(null);
    setSelectedTask(null);
    setSelectedAssignment(null);
    setIsFileProcessing(false);
    setUploadedFileName("");
    stopPolling?.();
  }, []);

  // ---------- file upload ----------
  const { handleFileUpload } = useFileUpload({
    storagePath: StoragePathType.ChatAttachments,
    beforeUpload: () => {
      setIsFileProcessing(true);
      setUploadedFileName("");
    },
    afterUpload: async (files: ProcessedFile[]) => {
      const file = files[0];
      setUploadedFileName(file.file.fileName || "Document");

      const res = await processAttachment({
        variables: { fileId: file.file.id },
      });

      const docId = res.data?.processCompanionChatAttachment?.data?.documentId;
      if (!docId) {
        setIsFileProcessing(false);
        setUploadedFileName("");
        removeAttachment();
        return;
      }
      setAttachment({
        type: AttachmentType.Document,
        id: docId,
        fileId: file.file.id,
      });
    },
    onError: removeAttachment,
    multiple: false,
  });

  const handleAddFile = useCallback(async () => {
    try {
      const files = await handleFileUpload();

      // User canceled picker
      if (!files || files.length === 0) {
        setIsFileProcessing(false);
        setUploadedFileName("");
        return;
      }
    } catch (err) {
      setIsFileProcessing(false);
      setUploadedFileName("");
      removeAttachment();
    }
  }, [handleFileUpload, removeAttachment]);

  // ---------- attachment display ----------
  const attachedTasks = useMemo(
    () =>
      attachment?.type === AttachmentType.Todo && selectedTask
        ? [
            {
              id: selectedTask.id,
              type: "task",
              name: selectedTask.name,
              duedate: selectedTask.date,
              isLoading: false,
            },
          ]
        : [],
    [attachment, selectedTask]
  );

  const attachedAssignments = useMemo(
    () =>
      attachment?.type === AttachmentType.Assignment && selectedAssignment
        ? [
            {
              id: selectedAssignment.id,
              type: "assignment",
              name: selectedAssignment.title,
              duedate: selectedAssignment.due_date,
              isLoading: false,
            },
          ]
        : [],
    [attachment, selectedAssignment]
  );

  const attachedFiles = useMemo(() => {
    if (attachment?.type !== AttachmentType.Document) return [];
    const file = data?.getDocument?.data?.files?.[0];
    return [
      {
        id: attachment.id,
        fileName: file?.fileName || uploadedFileName || "Document",
        isLoading: isFileProcessing,
      },
    ];
  }, [attachment, data, uploadedFileName, isFileProcessing]);

  // ---------- submit ----------
  const handleSend = () => {
    if (!value.trim()) return;

    onSubmit(value, attachment);
    setValue("");
    removeAttachment();
  };

  return (
    <>
      <ChatInput
        value={value}
        onChangeText={setValue}
        onSubmit={handleSend}
        disabled={disabled || isFileProcessing}
        placeholder={placeholder}
        showSuggestions={showSuggestions}
        activeWorkspaceType={activeWorkspaceType}
        attachedTasks={attachedTasks}
        attachedAssignments={attachedAssignments}
        attachedFiles={attachedFiles}
        onAddTask={() => setIsTaskModalVisible(true)}
        onAddAssignment={() => setIsAssignmentModalVisible(true)}
        onAddFile={handleAddFile}
        onRemoveAttachment={removeAttachment}
        taskModal={
          <AddTaskModal
            visible={isTaskModalVisible}
            onClose={() => setIsTaskModalVisible(false)}
            onSelectTask={handleSelectTask}
          />
        }
        assignmentModal={
          <AddAssignmentModal
            visible={isAssignmentModalVisible}
            onClose={() => setIsAssignmentModalVisible(false)}
            onSelectAssignment={handleSelectAssignment}
          />
        }
      />
    </>
  );
}
